import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Globe, Calendar, Star, MessageCircle, Facebook, Instagram, Linkedin, Award, Mail, Flag, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ReviewCard } from '@/components/reviews/ReviewCard';
import { WriteReviewModal } from '@/components/reviews/WriteReviewModal';
import { EditReviewModal } from '@/components/reviews/EditReviewModal';
import { ReportModal } from '@/components/reports/ReportModal';
import { ReportReviewModal } from '@/components/reviews/ReportReviewModal';
import { StarRating } from '@/components/reviews/StarRating';
import { useAuth } from '@/contexts/AuthContext';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalInput } from '@/components/ui/BrutalInput';
import {
  BrutalSelect,
  BrutalSelectTrigger,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectValue,
} from '@/components/ui/BrutalSelect';
import { useCategoryHelpers } from '@/hooks/useCategoryHelpers';
import { SocialShareDropdown } from '@/components/SocialShareDropdown';
import LoginGate from '@/components/auth/LoginGate';
import { SEO } from '@/components/SEO';
import { useContactTracking } from '@/hooks/useContactTracking';
import { sanitizeHtml } from '@/lib/sanitize';
import { formatInstagramUrl } from '@/lib/utils';

const ProfessionalProfile = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackContactClick } = useContactTracking();
  const { getCategoriesByIds, getCategoryName } = useCategoryHelpers();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewStats, setReviewStats] = useState<any>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [canReview, setCanReview] = useState(false);
  // State for reporting individual reviews
  const [reportReviewId, setReportReviewId] = useState<string | null>(null);
  const [reportReviewTitle, setReportReviewTitle] = useState<string>("");
  const [isReportReviewModalOpen, setIsReportReviewModalOpen] = useState(false);
  
  // State for editing own reviews
  const [editReview, setEditReview] = useState<any>(null);
  const [isEditReviewModalOpen, setIsEditReviewModalOpen] = useState(false);

  const openEditReviewModal = (review: any) => {
    setEditReview(review);
    setIsEditReviewModalOpen(true);
  };

  const closeEditReviewModal = () => {
    setEditReview(null);
    setIsEditReviewModalOpen(false);
  };

  const openReportReviewModal = (review: any) => {
    setReportReviewId(review.id);
    setReportReviewTitle(review.title);
    setIsReportReviewModalOpen(true);
  };

  const closeReportReviewModal = () => {
    setReportReviewId(null);
    setReportReviewTitle("");
    setIsReportReviewModalOpen(false);
  };
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');

  const handleContactClick = (contactType: 'email' | 'phone' | 'whatsapp' | 'website' | 'booking' | 'facebook' | 'instagram' | 'linkedin') => {
    if (profile?.id) {
      trackContactClick({ profileId: profile.id, contactType });
    }
  };

  useEffect(() => {
    loadProfile();
  }, [slug]);

  useEffect(() => {
    if (profile) {
      loadReviews();
      loadReviewStats();
      checkCanReview();
    }
  }, [profile, user, sortBy]);

  const loadProfile = async () => {
    try {
      let profileData: any = null;
      
      if (user) {
        // Authenticated users get full profile data including contacts
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('slug', slug)
          .single();
        
        if (error) throw error;
        profileData = data;
      } else {
        // Anonymous users get public view (excludes sensitive contact data)
        const { data, error } = await supabase
          .from('profiles_public' as any)
          .select('*')
          .eq('slug', slug)
          .single();
        
        if (error) throw error;
        profileData = data;
      }

      if (!profileData) {
        navigate('/404');
        return;
      }

      // Fetch subscription type for this profile's user
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('product_type')
        .eq('user_id', profileData.user_id)
        .eq('status', 'active')
        .in('product_type', ['base', 'pro', 'plus'])
        .gte('current_period_end', new Date().toISOString())
        .maybeSingle();

      // Fetch user badges
      const { data: userBadges } = await supabase
        .from('user_badges')
        .select(`
          badge_id,
          badges (
            id, description_it, description_en, description_es,
            image_url, is_active
          )
        `)
        .eq('user_id', profileData.user_id);

      setProfile({ 
        ...profileData, 
        subscription_type: subscription?.product_type || null,
        userBadges: userBadges?.filter((ub: any) => ub.badges?.is_active && ub.badges?.image_url) || []
      });
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
      navigate('/404');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeDescription = (badge: any) => {
    if (i18n.language === 'it') return badge.description_it;
    if (i18n.language === 'en') return badge.description_en || badge.description_it;
    if (i18n.language === 'es') return badge.description_es || badge.description_it;
    return badge.description_it || '';
  };

  const getBio = () => {
    if (i18n.language === 'it') return profile.bio_it;
    if (i18n.language === 'en') return profile.bio_en;
    if (i18n.language === 'es') return profile.bio_es;
    return profile.bio_it;
  };

  const getDescription = () => {
    if (i18n.language === 'it') return profile.description_it;
    if (i18n.language === 'en') return profile.description_en;
    if (i18n.language === 'es') return profile.description_es;
    return profile.description_it;
  };

  const getEmbedUrl = (url: string) => {
    if (!url) return null;
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    return url;
  };

  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      // Step 1: Load reviews without join
      let query = supabase
        .from('reviews')
        .select('*')
        .eq('profile_id', profile.id)
        .eq('admin_approved', true);

      if (sortBy === 'recent') {
        query = query.order('created_at', { ascending: false });
      } else if (sortBy === 'helpful') {
        query = query.order('helpful_count', { ascending: false });
      } else if (sortBy === 'rating_high') {
        query = query.order('rating', { ascending: false });
      } else if (sortBy === 'rating_low') {
        query = query.order('rating', { ascending: true });
      }

      const { data, error } = await query;
      if (error) throw error;

      let reviewsData = data || [];

      // Step 2: Load reviewer names in batch
      if (reviewsData.length > 0) {
        const userIds = [...new Set(reviewsData.map(r => r.user_id))];
        const { data: reviewerProfiles } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, profile_image')
          .in('user_id', userIds);

        // Map names to reviews
        const reviewerMap = new Map(reviewerProfiles?.map(p => [p.user_id, p]) || []);
        reviewsData = reviewsData.map(review => ({
          ...review,
          reviewer: reviewerMap.get(review.user_id) || null
        }));
      }

      // Apply search filter
      if (searchQuery) {
        reviewsData = reviewsData.filter((review) =>
          review.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          review.comment.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadReviewStats = async () => {
    try {
      const { data } = await supabase.rpc('calculate_profile_rating', {
        p_profile_id: profile.id,
      });

      if (data && data.length > 0) {
        setReviewStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading review stats:', error);
    }
  };

  const checkCanReview = async () => {
    if (!user) {
      setCanReview(false);
      return;
    }

    try {
      const { data } = await supabase
        .from('reviews')
        .select('id')
        .eq('profile_id', profile.id)
        .eq('user_id', user.id)
        .maybeSingle();

      setCanReview(!data);
    } catch (error) {
      console.error('Error checking review permission:', error);
    }
  };

  // SEO: Generate dynamic meta description
  const seoData = useMemo(() => {
    if (!profile) return null;
    
    const lang = i18n.language.substring(0, 2) as 'it' | 'en' | 'es';
    const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    const categories = (profile.categories || [])
      .map((catId: string) => getCategoryName(catId, lang))
      .slice(0, 3)
      .join(', ');
    
    const location = profile.address_city || '';
    const services: string[] = [];
    if (profile.in_person_service) services.push(lang === 'it' ? 'in presenza' : lang === 'es' ? 'presencial' : 'in person');
    if (profile.online_service) services.push('online');
    const serviceText = services.length > 0 ? `. ${lang === 'it' ? 'Consulenze' : lang === 'es' ? 'Consultas' : 'Consultations'} ${services.join(lang === 'it' ? ' e ' : lang === 'es' ? ' y ' : ' and ')}` : '';
    
    const title = `${name} - ${profile.business_name} ${lang === 'it' ? 'Inclusivo' : lang === 'es' ? 'Inclusivo' : 'Inclusive'}`;
    const description = `${name} ${lang === 'it' ? 'è' : lang === 'es' ? 'es' : 'is'} ${profile.business_name}${categories ? ` ${lang === 'it' ? 'specializzato in' : lang === 'es' ? 'especializado en' : 'specialized in'} ${categories}` : ''}${location ? ` ${lang === 'it' ? 'a' : lang === 'es' ? 'en' : 'in'} ${location}` : ''}${serviceText}. ${lang === 'it' ? 'Professionista inclusivo verificato su Inclusia.' : lang === 'es' ? 'Profesional inclusivo verificado en Inclusia.' : 'Inclusive professional verified on Inclusia.'}`;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": ["Person", "LocalBusiness"],
      "name": name,
      "jobTitle": profile.business_name,
      "image": profile.profile_image,
      "url": `${window.location.origin}/professionista/${profile.slug}`,
      ...(profile.address_city && {
        "address": {
          "@type": "PostalAddress",
          "addressLocality": profile.address_city,
          ...(profile.address_region && { "addressRegion": profile.address_region }),
          ...(profile.address_country && { "addressCountry": profile.address_country })
        }
      }),
      ...(profile.languages_spoken && profile.languages_spoken.length > 0 && {
        "knowsLanguage": profile.languages_spoken
      }),
      ...(profile.average_rating && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": profile.average_rating,
          "reviewCount": profile.review_count || 0
        }
      })
    };
    
    return { title, description, schema };
  }, [profile, i18n.language]);


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Skeleton className="h-64 w-full mb-8" />
          <Skeleton className="h-32 w-full" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {seoData && (
        <SEO
          title={seoData.title}
          description={seoData.description}
          image={profile.profile_image}
          type="profile"
          lang={i18n.language.substring(0, 2) as 'it' | 'en' | 'es'}
          schema={seoData.schema}
          articleAuthor={`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}
        />
      )}
      <Header />
      
      {/* Cover Image - Full Width */}
      {profile.cover_image && (
        <div className="w-full">
          <img
            src={profile.cover_image}
            alt="Cover"
            className="w-full h-auto border-b-[3px] border-dark"
            loading="lazy"
          />
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Single Container for All Sections */}
          <div className="border-[3px] border-dark bg-surface shadow-[8px_8px_0px_0px] shadow-dark">
          
          {/* Profile Header */}
          <div className="px-6 md:px-8 py-4 md:py-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {profile.profile_image && (
                <img
                  src={profile.profile_image}
                  alt={profile.business_name}
                  className="w-40 h-40 rounded-full border-[4px] border-dark object-cover shadow-[8px_8px_0px_0px] shadow-dark"
                  loading="lazy"
                />
              )}
              
              <div className="flex-1 w-full">
                <div className="mb-6">
                  <SEOBreadcrumb 
                    items={[
                      { label: t('common.professionals'), href: '/professionals' },
                      { label: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.business_name }
                    ]}
                    align="left"
                  />
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h1 className="font-heading text-3xl md:text-5xl font-black text-dark">
                      {profile.first_name} {profile.last_name}
                    </h1>
                    {profile.subscription_type === 'plus' && (
                      <Badge className="bg-purple-500 text-white text-xs px-2 py-1 border-0">
                        PLUS
                      </Badge>
                    )}
                    {profile.subscription_type === 'pro' && (
                      <Badge className="bg-blue-500 text-white text-xs px-2 py-1 border-0">
                        PRO
                      </Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-muted-foreground">{profile.business_name}</p>
                  
                  {/* User Badges - Solo PNG con tooltip */}
                  {profile.userBadges && profile.userBadges.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <TooltipProvider>
                        {profile.userBadges.map((ub: any) => (
                          <Tooltip key={ub.badge_id}>
                            <TooltipTrigger asChild>
                              <img 
                                src={ub.badges.image_url} 
                                alt="Badge" 
                                className="w-8 h-8 object-contain cursor-help hover:scale-110 transition-transform"
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm">
                                {getBadgeDescription(ub.badges)}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </TooltipProvider>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Specializzazioni */}
                  {profile.categories && profile.categories.length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        Specializzazioni
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {getCategoriesByIds(profile.categories).map((cat) => (
                          <span 
                            key={cat.id} 
                            className="px-3 py-1 bg-primary/10 border border-dark/20 text-sm font-medium"
                          >
                            {cat?.[i18n.language.substring(0, 2) as 'it' | 'en' | 'es']?.name || cat?.it?.name || cat?.id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Zona e Modalità inline */}
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    {/* Zona */}
                    {(profile.address_city || profile.address_region) && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          Zona
                        </h3>
                        <span className="text-muted-foreground text-sm">
                          {profile.address_city}
                          {profile.address_region && `, ${profile.address_region}`}
                        </span>
                      </div>
                    )}

                    {/* Modalità */}
                    {(profile.in_person_service || profile.online_service) && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          Modalità
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {profile.in_person_service && (
                            <span className="px-3 py-1 bg-primary/10 border border-dark/20 text-sm font-medium">
                              In presenza{profile.service_radius_km && ` • ${profile.service_radius_km}km`}
                            </span>
                          )}
                          {profile.online_service && (
                            <span className="px-3 py-1 bg-secondary/10 border border-dark/20 text-sm font-medium">
                              Online
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Lingue parlate */}
                  {profile.languages_spoken && profile.languages_spoken.length > 0 && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Lingue
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {profile.languages_spoken.map((lang: string) => (
                          <span 
                            key={lang} 
                            className="px-3 py-1 bg-accent/10 border border-dark/20 text-sm font-medium"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <LoginGate 
                  title={t('auth.gate.viewContacts')} 
                  description={t('auth.gate.registerFreeToSeeContacts')}
                  variant="inline"
                >
                  {/* Contacts */}
                  <div className="mt-6">
                    <h2 className="font-heading text-xl font-bold mb-3">
                      {t('profile.contactsOf', { name: `${profile.first_name} ${profile.last_name}` })}
                    </h2>
                    <TooltipProvider>
                      <div className="flex flex-wrap items-center gap-3">
                        {profile.phone && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={`tel:${profile.phone}`}
                                onClick={() => handleContactClick('phone')}
                                className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                              >
                                <Phone className="w-5 h-5" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t('profile.call')}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        
                        {profile.professional_email && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={`mailto:${profile.professional_email}`}
                                onClick={() => handleContactClick('email')}
                                className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                              >
                                <Mail className="w-5 h-5" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Email</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        
                        {profile.whatsapp && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={`https://wa.me/${profile.whatsapp}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleContactClick('whatsapp')}
                                className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                              >
                                <MessageCircle className="w-5 h-5" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>WhatsApp</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        
                        {profile.website && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={profile.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => handleContactClick('website')}
                                className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 font-heading font-bold shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                              >
                                <Globe className="w-5 h-5" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t('profile.website')}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        
                        {(profile.social_facebook || profile.social_instagram || profile.social_linkedin) && (
                          <div className="flex gap-3">
                            {profile.social_facebook && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <a
                                    href={profile.social_facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleContactClick('facebook')}
                                    className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                  >
                                    <Facebook className="w-5 h-5" />
                                  </a>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Facebook</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            {profile.social_instagram && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <a
                                    href={formatInstagramUrl(profile.social_instagram) || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleContactClick('instagram')}
                                    className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                  >
                                    <Instagram className="w-5 h-5" />
                                  </a>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Instagram</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            
                            {profile.social_linkedin && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <a
                                    href={profile.social_linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleContactClick('linkedin')}
                                    className="inline-flex items-center justify-center w-11 h-11 border-[3px] border-dark bg-card text-foreground hover:bg-card/80 shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] transition-all"
                                  >
                                    <Linkedin className="w-5 h-5" />
                                  </a>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>LinkedIn</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        )}
                        
                        <div className="ml-auto">
                          <SocialShareDropdown 
                            url={`https://inclusia.pro/professional/${profile.slug}`}
                            title={profile.business_name}
                            description={getBio() || ''}
                          />
                        </div>
                      </div>
                    </TooltipProvider>
                  </div>
                </LoginGate>

                {/* Biography and Video */}
                {(getBio() || profile.video_url) && (
                  <div className="mt-6">
                    <h2 className="font-heading text-xl font-bold mb-3">
                      {t('profile.whoIs', { name: `${profile.first_name} ${profile.last_name}` })}
                    </h2>
                    {getBio() && (
                      <div 
                        className="text-sm leading-relaxed mb-4 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: sanitizeHtml(getBio(), {
                            allowedTags: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'h2', 'blockquote']
                          }) 
                        }}
                      />
                    )}
                    
                    {profile.video_url && getEmbedUrl(profile.video_url) && (
                      <div className="aspect-video border-[3px] border-dark overflow-hidden shadow-[4px_4px_0px_0px] shadow-dark">
                        <iframe
                          src={getEmbedUrl(profile.video_url)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Services */}
                {(getDescription() || profile.price_range) && (
                  <div className="mt-6">
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                      <h2 className="font-heading text-xl font-bold">
                        {t('profile.servicesOf', { name: `${profile.first_name} ${profile.last_name}` })}
                      </h2>
                      {profile.price_range && (
                        <span className="px-3 py-1 bg-accent/10 border border-dark/20 text-sm font-medium">
                          {profile.price_range}
                        </span>
                      )}
                    </div>
                    
                    {getDescription() && (
                      <div className="mb-4">
                        <div 
                          className="text-sm leading-relaxed prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ 
                            __html: sanitizeHtml(getDescription(), {
                              allowedTags: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'h2', 'blockquote']
                            }) 
                          }}
                        />
                      </div>
                    )}
                    
                    {profile.booking_url && user && (
                      <div className="mt-4">
                        <BrutalButton
                          variant="primary"
                          onClick={() => window.open(profile.booking_url!, '_blank')}
                          className="w-full sm:w-auto"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {t('profile.bookWith', { name: `${profile.first_name} ${profile.last_name}` })}
                        </BrutalButton>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator className="h-[3px] bg-dark" />

          {/* Reviews Section */}
          <div className="px-6 md:px-8 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
            <div>
              <div className="flex justify-between items-start">
                <h2 className="font-heading text-3xl font-bold mb-2">
                  {t('reviews.title')} ({reviewStats?.total_reviews || 0})
                </h2>
                {/* Mobile only: report icon */}
                {user && (
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="sm:hidden p-2 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label={t('reports.reportProfile')}
                  >
                    <Flag className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Mobile only: button under title */}
              {canReview && (
                <BrutalButton onClick={() => setShowReviewModal(true)} className="sm:hidden mb-4">
                  {t('reviews.writeReview')}
                </BrutalButton>
              )}

              {reviewStats && reviewStats.average_rating && (
                <div className="flex items-center gap-2">
                  <span className="text-4xl font-bold mr-2">
                    {reviewStats.average_rating}
                  </span>
                  <StarRating rating={reviewStats.average_rating} size="lg" readonly />
                  
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="ml-2 p-1.5 hover:bg-muted transition-colors">
                        <Info className="w-4 h-4 text-muted-foreground hover:text-dark" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="start" className="w-64 bg-card border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark p-4">
                      <p className="font-heading font-bold uppercase text-sm mb-3">{t('reviews.ratingDistribution')}</p>
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map((stars) => (
                          <div key={stars} className="flex items-center gap-2">
                            <span className="text-sm font-bold w-8">{stars} ★</span>
                            <div className="flex-1 h-3 bg-muted relative border-2 border-dark">
                              <div
                                className="h-full bg-primary"
                                style={{
                                  width: `${
                                    reviewStats.total_reviews > 0
                                      ? ((reviewStats.rating_breakdown[stars] || 0) /
                                          reviewStats.total_reviews) *
                                        100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                            <span className="text-sm text-muted-foreground w-6 text-right">
                              {reviewStats.rating_breakdown[stars] || 0}
                            </span>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-2">
              {canReview && (
                <BrutalButton onClick={() => setShowReviewModal(true)}>
                  {t('reviews.writeReview')}
                </BrutalButton>
              )}
              {user && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setShowReportModal(true)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                        aria-label={t('reports.reportProfile')}
                      >
                        <Flag className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('reports.reportProfile')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {/* Review Filters */}
          <div className="flex gap-2 mb-6">
            <BrutalSelect value={sortBy} onValueChange={(v) => setSortBy(v)}>
              <BrutalSelectTrigger className="w-auto min-w-[180px]">
                <BrutalSelectValue />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                <BrutalSelectItem value="recent">{t('reviews.mostRecent')}</BrutalSelectItem>
                <BrutalSelectItem value="helpful">{t('reviews.mostHelpful')}</BrutalSelectItem>
                <BrutalSelectItem value="rating_high">{t('reviews.highestRating')}</BrutalSelectItem>
                <BrutalSelectItem value="rating_low">{t('reviews.lowestRating')}</BrutalSelectItem>
              </BrutalSelectContent>
            </BrutalSelect>

            <BrutalInput
              type="text"
              placeholder={t('reviews.searchReviews')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
          </div>

          {/* Reviews List */}
          {reviewsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 bg-surface border-[3px] border-dark">
              <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {t('reviews.noReviewsYet')}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  currentUserId={user?.id}
                  onVote={loadReviews}
                  onReport={() => openReportReviewModal(review)}
                  onEdit={openEditReviewModal}
                />
              ))}
            </div>
          )}
          </div>
          
          </div>
        </div>
      </main>

      <WriteReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        profileId={profile?.id}
        professionalName={`${profile?.first_name} ${profile?.last_name}`}
        onSuccess={() => {
          loadReviews();
          loadReviewStats();
          checkCanReview();
        }}
      />

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        profileId={profile?.id}
        targetName={`${profile?.first_name} ${profile?.last_name}`}
      />

      <ReportReviewModal
        reviewId={reportReviewId || ""}
        reviewTitle={reportReviewTitle}
        isOpen={isReportReviewModalOpen}
        onClose={closeReportReviewModal}
      />

      <EditReviewModal
        isOpen={isEditReviewModalOpen}
        onClose={closeEditReviewModal}
        review={editReview}
        professionalName={`${profile?.first_name} ${profile?.last_name}`}
        onSuccess={() => {
          loadReviews();
          loadReviewStats();
        }}
      />

      <Footer />
    </div>
  );
};

export default ProfessionalProfile;
